import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { constantsData, importerConfigData, statusTypesData } from "@/data";
import { createReleaseOwnershipMutation, getReleaseQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useCreateReleaseOwnership = (withoutRefetch, withoutAlert) => {
    const [controller, setController] = useState(null);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const release = useSelector((state) => state.releasesReducer).currentRelease;

    const [createReleaseOwnershipAction, { loading }] = useMutation(createReleaseOwnershipMutation, {
        ...(!withoutRefetch && {
            awaitRefetchQueries: true,
            refetchQueries: [
                {
                    query: getReleaseQuery,
                    variables: {
                        where: { id: release?.id },
                    },
                },
            ],
        }),
    });

    const createReleaseOwnership = async ({ cLine, label, pLine, territory }, id, importerData) => {
        if (import.meta.env.VITE_ENABLE_DEBUG_LOGS === "true" && importerData) {
            console.log("releaseIdFromCreateReleaseOwnershipHook", id);
        }

        try {
            const newController = new AbortController();

            setController(newController);

            importerData?.setCurrentStep(importerConfigData.steps.creatingOwnership);

            await createReleaseOwnershipAction({
                context: {
                    fetchOptions: { signal: newController.signal },
                },
                onCompleted: () => {
                    if (!withoutAlert) {
                        dispatch(
                            openAlert(
                                t("messages.createMsgs.success", {
                                    entity: `${t("releases.entity")} ${t("labels.ownership")}`,
                                }),
                                statusTypesData.success,
                            ),
                        );
                    }

                    if (importerData) {
                        importerData?.setStatus((prev) => ({
                            ...prev,
                            release: {
                                ...prev.release,
                                ownershipCreated: true,
                            },
                        }));
                    }
                },
                variables: {
                    data: {
                        c_line: cLine,
                        label,
                        p_line: pLine,
                        release: {
                            connect: { id: importerData ? id : release?.id },
                        },
                        territory: {
                            connect: { id: territory },
                        },
                    },
                },
            });
        } catch (err) {
            if (!withoutAlert) {
                displayErrorHandler(
                    err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                    dispatch,
                    openAlert,
                );
            }

            if (importerData) {
                dispatch(
                    importerData?.setInvalidImporterRelease({
                        originalData: importerData?.originalData,
                        release: {
                            data: importerData?.release,
                            message:
                                err.message === constantsData.failedToFetch
                                    ? t("messages.networkFailure")
                                    : err.message,
                        },
                        ...(err.message === constantsData.failedToFetch && { isNetworkFailed: true }),
                    }),
                );

                importerData?.setStatus((prev) => ({
                    ...prev,
                    release: {
                        ...prev.release,
                        loading: false,
                        ownershipCreated: false,
                        success: false,
                    },
                }));
            }
        } finally {
            setController(null);

            if (importerData) importerData?.setCurrentStep(null);
        }
    };

    return {
        cancelRequest: () => controller && controller.abort(),
        createReleaseOwnership,
        loading,
    };
};

export default useCreateReleaseOwnership;
